import type { SignificanceDisplayItem } from '@/state/experiments/useExperimentStats';

/**
 * Plain-language readings of the significance table.
 *
 * The table is correct but unreadable without training: a z-score, a p-value and
 * a lift interval do not tell a marketer whether to ship the variant. Each row
 * gets one sentence saying what happened and one saying what to do, derived from
 * the same numbers rather than restated from them, so the prose cannot drift
 * from the table.
 *
 * Kept as a pure function so the wording is testable, in particular the cases
 * where the columns disagree (significant but the interval spans zero) which is
 * exactly where a hand-written sentence would mislead.
 */

export type Verdict = 'winner' | 'loser' | 'inconclusive' | 'unknown';

export type SignificanceExplanation = {
  id: number;
  name: string;
  verdict: Verdict;
  /** What the numbers say, in one sentence. */
  reading: string;
  /** What to do about it, in one sentence. */
  advice: string;
};

const pct = (value: number) => `${value >= 0 ? '+' : ''}${(value * 100).toFixed(2)}%`;

/** A percentage stated as a plain chance rather than a decimal probability. */
const chance = (probability: number) => {
  const asPercent = probability * 100;
  if (asPercent < 0.1) return 'less than 0.1%';
  if (asPercent > 99.9) return 'more than 99.9%';
  return `${asPercent.toFixed(1)}%`;
};

const frequentistExplanation = (item: SignificanceDisplayItem): SignificanceExplanation => {
  const base = { id: item.id, name: item.name };
  const lift = item.liftInterval;
  const significant = item.significant === true;

  if (!lift || item.pValue === undefined) {
    return {
      ...base,
      verdict: 'unknown',
      reading: `There is not enough data yet to compare ${item.name} against the original.`,
      advice: 'Let the experiment collect more traffic before reading anything into it.',
    };
  }

  const midpoint = (lift.lower + lift.upper) / 2;
  const spansZero = lift.lower < 0 && lift.upper > 0;

  // The p-value test and the interval can disagree at the boundary, and after a
  // multiple-comparison correction they routinely do: the correction raises the
  // bar for significance but the interval is not widened to match. Trust the
  // interval, because it is the one that says which direction the effect runs.
  if (significant && !spansZero) {
    const winning = lift.lower > 0;
    return {
      ...base,
      verdict: winning ? 'winner' : 'loser',
      reading: winning
        ? `${item.name} is genuinely ahead of the original, by somewhere between ${pct(lift.lower)} and ${pct(lift.upper)}.`
        : `${item.name} is genuinely behind the original, by somewhere between ${pct(Math.abs(lift.upper))} and ${pct(Math.abs(lift.lower))}.`,
      advice: winning
        ? `A difference this large would show up by chance only ${chance(item.pValue)} of the time, so this is safe to act on.`
        : 'The evidence that this variant hurts is solid. Stop sending traffic to it.',
    };
  }

  if (significant && spansZero) {
    return {
      ...base,
      verdict: 'inconclusive',
      reading: `${item.name} passes the significance test, but the plausible range for its true effect (${pct(lift.lower)} to ${pct(lift.upper)}) still includes "no difference at all".`,
      advice:
        'Treat this as unresolved rather than a win: the two measures disagree, which happens at the edge of significance.',
    };
  }

  return {
    ...base,
    verdict: 'inconclusive',
    reading: `${item.name} is currently ${pct(midpoint)} against the original, but the true effect could be anywhere from ${pct(lift.lower)} to ${pct(lift.upper)}, including no difference.`,
    advice: `A swing this size turns up by chance ${chance(item.pValue)} of the time, which is too often to call it a result. Keep the test running.`,
  };
};

const bayesianExplanation = (item: SignificanceDisplayItem): SignificanceExplanation => {
  const base = { id: item.id, name: item.name };
  const probability = item.probabilityOfBeingBest;

  if (probability === undefined) {
    return {
      ...base,
      verdict: 'unknown',
      reading: `There is not enough data yet to judge ${item.name}.`,
      advice: 'Let the experiment collect more traffic.',
    };
  }

  const loss = item.expectedLoss ?? 0;
  // Expected loss is what you give up if you pick this variant and it turns out
  // not to be the best: the number that actually makes the decision, and the one
  // most easily misread as a performance figure.
  const lossPhrase = `if you chose it and it turned out not to be the best, you would expect to give up about ${(loss * 100).toFixed(2)}%`;

  if (probability >= 0.95) {
    return {
      ...base,
      verdict: 'winner',
      reading: `${item.name} has a ${chance(probability)} chance of being the best option of those tested.`,
      advice: `That is about as certain as this gets: ${lossPhrase}, which is why it is safe to ship.`,
    };
  }

  if (probability >= 0.6) {
    return {
      ...base,
      verdict: 'inconclusive',
      reading: `${item.name} is the front-runner with a ${chance(probability)} chance of being best, but it is not a settled result.`,
      advice: `Roughly a ${chance(1 - probability)} chance remains that something else is better, and ${lossPhrase}.`,
    };
  }

  if (probability <= 0.05) {
    return {
      ...base,
      verdict: 'loser',
      reading: `${item.name} has only a ${chance(probability)} chance of being the best option.`,
      advice: 'It is very unlikely to be the right choice. Stop sending traffic to it.',
    };
  }

  return {
    ...base,
    verdict: 'inconclusive',
    reading: `${item.name} has a ${chance(probability)} chance of being the best option, which is not enough to choose on.`,
    advice: `Keep the test running, ${lossPhrase}.`,
  };
};

export const explainSignificance = (
  items: readonly SignificanceDisplayItem[],
  method: 'frequentist' | 'bayesian',
): SignificanceExplanation[] =>
  items.map((item) => (method === 'bayesian' ? bayesianExplanation(item) : frequentistExplanation(item)));

/**
 * What each column means, in the order the table shows them. Stated as "what it
 * answers" rather than as a definition, because the definition is what the user
 * already could not read.
 */
export const COLUMN_GLOSSARY: Record<'frequentist' | 'bayesian', { term: string; meaning: string }[]> = {
  frequentist: [
    {
      term: 'p-value',
      meaning:
        'How often a difference this big would appear purely by chance if the variant changed nothing. Lower means harder to explain away; below 0.05 is the usual bar.',
    },
    {
      term: 'Lift CI',
      meaning:
        'The range the real effect is most likely to sit in. If the whole range is above zero the variant is winning; if it straddles zero, "no difference" is still on the table.',
    },
    {
      term: 'z-score',
      meaning:
        'How many standard errors separate the variant from the original: the raw distance behind the p-value. Its sign shows the direction.',
    },
    {
      term: 'SPRT result',
      meaning:
        'A running check that can call a result early, so you can stop as soon as the answer is clear rather than waiting for a fixed sample size.',
    },
  ],
  bayesian: [
    {
      term: 'Prob. best',
      meaning: 'The chance this variant is the best of the ones being tested. Read it directly as a probability.',
    },
    {
      term: 'Expected loss',
      meaning:
        'What you would expect to give up by picking this variant if it is not actually the best. A small number means choosing it is cheap even if you are wrong.',
    },
    {
      term: 'Credible interval',
      meaning: "The range the variant's own conversion rate most likely falls in.",
    },
    {
      term: 'Lift CI',
      meaning: 'The range for the difference against the original, on the same reading as the credible interval.',
    },
  ],
};
